import React, { useCallback, useEffect, useRef, useState } from "react";

import { getPageScrollTop } from "@/lib/pageScroll";

/**
 * A horizontal pager for full pages that still scroll vertically.
 *
 * WHY IT IS BUILT THIS WAY. The obvious implementations both break something
 * these panes rely on:
 *
 *   - A native `overflow-x: auto` scroll-snap strip turns the pager into a
 *     scroll container, which means `position: sticky` inside a pane (the
 *     Potential pane's pinned filter rows) sticks to a box that never scrolls —
 *     i.e. it silently stops working.
 *   - Laying all panes out in a flex row makes the pager as tall as the TALLEST
 *     pane, so a short Completed history would leave a screenful of dead space
 *     under the Potential pane.
 *
 * So: the active pane is in normal flow (it alone sets the height), the panes
 * either side are absolutely positioned one viewport-width out, and the whole
 * track is moved with a transform. The wrapper uses `overflow-x: clip` rather
 * than `hidden` precisely because clip does NOT create a scroll container —
 * sticky descendants keep working, and vertical overflow stays visible so the
 * page scrolls as it always did.
 *
 * Vertical scrolling is never intercepted: nothing is preventDefault-ed, so a
 * drag that goes up or down is the browser's, and it hands us a pointercancel
 * when it takes over.
 *
 * THE WRAPPER SETS `touch-action: pan-y` (2026-09-29). It used to set nothing,
 * on the belief that touch-action is intersected all the way down the hit-test
 * path and a pan-y pager would therefore freeze the Potential pane's
 * horizontally scrolling pill rows. That reading was wrong: the effective
 * touch-action is computed from the touched element up to the NEAREST ancestor
 * that implements the default touch behaviour, and an overflow-x:auto pill row
 * is its own scroll container, so the pager's value never enters that chain and
 * the pills keep scrolling. What the missing pan-y actually did was break the
 * pager itself: with touch-action auto, Chromium (the Android WebView) starts
 * its own scroll gesture once a touch moves past the touch slop (~8px) in ANY
 * direction, scrollable or not, and fires pointercancel with no further
 * pointermoves, so the pager never reached its 14px lock and nothing slid
 * (Galaxy S21+, owner report). WebKit began a VERTICAL pan as soon as a thumb
 * arc drifted ~10pt and cancelled the same way, so on the iPhone only a
 * perfectly level swipe landed. With pan-y both engines let a mostly-horizontal
 * drag run to pointerup. A gesture that begins inside a horizontally-scrollable
 * child is still disowned (see `startsInHorizontalScroller`) so the pills
 * scroll instead of the page turning.
 *
 * Panes are mounted lazily and then kept — you pay for a pane the first time
 * it is reachable, not on every swipe.
 */

/**
 * True when the gesture started inside something that scrolls sideways of its
 * own accord (a pill row, a code block, a carousel). That child owns the
 * horizontal axis; the pager must not also consume it.
 */
function startsInHorizontalScroller(target: EventTarget | null, stopAt: HTMLElement | null): boolean {
  let node = target instanceof HTMLElement ? target : null;
  while (node && node !== stopAt) {
    if (node.scrollWidth > node.clientWidth + 1) {
      const overflowX = getComputedStyle(node).overflowX;
      if (overflowX === "auto" || overflowX === "scroll") return true;
    }
    node = node.parentElement;
  }
  return false;
}
export function SwipePager({
  index,
  onIndexChange,
  panes,
  labelledBy,
  className = "",
}: {
  index: number;
  onIndexChange: (next: number) => void;
  /** One entry per pane. `key` is also used for the tabpanel's element id. */
  panes: Array<{ key: string; node: React.ReactNode }>;
  /**
   * id of the tab that names the ACTIVE pane. Only the active pane gets it:
   * off-stage panes are aria-hidden anyway, and pointing them at a control that
   * isn't in the accessibility tree would be a dangling reference.
   */
  labelledBy?: string;
  className?: string;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [dx, setDx] = useState(0);
  const [animating, setAnimating] = useState(false);
  // Panes are mounted on first reach and kept: swiping back and forth should
  // not re-mount (and so re-render every lead tile) each time.
  const [mounted, setMounted] = useState<Set<number>>(() => new Set([index]));
  // Where a settle animation is heading. Read in the transition-end handler so
  // a gesture that lands mid-animation can't commit the wrong pane.
  const pendingRef = useRef<number | null>(null);

  const gesture = useRef<{
    id: number;
    startX: number;
    startY: number;
    /** Last x seen on pointermove — see the note in `endGesture`. */
    lastX: number;
    locked: boolean;
    dead: boolean;
  } | null>(null);

  useEffect(() => {
    setMounted((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
  }, [index]);

  /*
   * THE PAGER IS ALSO THE TOUCH SURFACE, so it has to reach the bottom of the
   * screen. Its height comes from the pane in flow, and Completed is often a
   * handful of rows, so the pager ended part-way down and everything below it
   * was AppShell: a swipe that started in that empty space reached no pointer
   * handler at all and did nothing. Reported on device 2026-08-13, and a fixed
   * 60vh floor did not fix it because the gap is whatever the pane does not
   * fill, not a constant.
   *
   * So the floor is measured: the distance from the pager's own top edge down
   * to the bottom of the window. Taken in document space (rect + page scroll)
   * so a scrolled page measures the same as an unscrolled one, and the parent's
   * bottom padding is deducted so filling the gap does not push the page taller
   * than the viewport and invent a scroll where there was none.
   *
   * It is a FLOOR, not a height: a pane longer than the screen still sets its
   * own, and only the leftover is claimed.
   */
  const [floorPx, setFloorPx] = useState<number | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const measure = () => {
      const el = wrapRef.current;
      if (!el) return;
      const docTop = el.getBoundingClientRect().top + getPageScrollTop();
      const parent = el.parentElement;
      const padBottom = parent ? parseFloat(getComputedStyle(parent).paddingBottom) || 0 : 0;
      const avail = Math.round(window.innerHeight - docTop - padBottom);
      setFloorPx(avail > 0 ? avail : null);
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, []);

  const width = () => wrapRef.current?.clientWidth ?? 0;

  const reduceMotion = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /** Settle to `next`, sliding one pane-width when the move is a single step. */
  const goTo = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(panes.length - 1, next));
      if (clamped === index) {
        setAnimating(true);
        setDx(0);
        pendingRef.current = null;
        return;
      }
      setMounted((prev) => (prev.has(clamped) ? prev : new Set(prev).add(clamped)));
      // A multi-step jump (tapping the far tab) has no honest single-width
      // animation, so it commits immediately rather than sliding through a
      // pane it never shows.
      if (Math.abs(clamped - index) !== 1 || reduceMotion() || width() === 0) {
        setAnimating(false);
        setDx(0);
        pendingRef.current = null;
        onIndexChange(clamped);
        return;
      }
      // A drag that already reached the full pane width has nothing left to
      // animate, and an unchanged transform fires no transitionend — which
      // would strand `animating` true and freeze the pager for good, since
      // pointerdown ignores everything while it is set. Commit those outright.
      const target = clamped > index ? -width() : width();
      if (Math.abs(target - dx) < 1) {
        setAnimating(false);
        setDx(0);
        pendingRef.current = null;
        onIndexChange(clamped);
        return;
      }
      pendingRef.current = clamped;
      setAnimating(true);
      setDx(target);
    },
    [dx, index, onIndexChange, panes.length],
  );

  /**
   * The transform and the pane offsets cancel out here: as `index` moves by one
   * the active pane's `left` shifts by exactly the width we had translated, so
   * committing both in the same render is visually a no-op. `animating` drops
   * in the same update, which is what stops the reset from being animated back.
   *
   * THE GUARD IS LOAD-BEARING. `transitionend` BUBBLES, and these panes are full
   * of transitions — tab colours, chevron rotations, hover elevations. Any one
   * of them finishing anywhere inside a pane used to land here and consume the
   * pending index, so a swipe would reset without committing and the pane
   * snapped back. That is why sliding appeared to work in one direction only:
   * whether it survived depended on which children happened to be animating.
   * Only this element's own transform counts.
   */
  const settle = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    setAnimating(false);
    setDx(0);
    if (pending != null) onIndexChange(pending);
  }, [onIndexChange]);

  const onTransitionEnd = (e: React.TransitionEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== "transform") return;
    settle();
  };

  /**
   * A backstop for a settle that never reports back.
   *
   * `transitionend` does not fire if the transform never actually changes —
   * which happens when a drag ends exactly at the pane width — and it does not
   * fire for a transition the browser drops under load. Either way the pending
   * index was stranded: `animating` stayed true so the pager ignored every
   * pointerdown, and the NEXT gesture's transitionend committed the OLD pending
   * pane before its own, which reads as a swipe taking two goes and then
   * over-shooting. Reported on device 2026-08-13 as "a double effect".
   *
   * Comfortably longer than the 260ms transition, so a healthy animation always
   * lands on its own and this never races it.
   */
  useEffect(() => {
    if (!animating) return;
    const t = window.setTimeout(settle, 420);
    return () => window.clearTimeout(t);
  }, [animating, settle]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (animating) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    /*
     * ONE GESTURE AT A TIME. A second contact used to overwrite the live one,
     * and then every pointermove from the FIRST finger was discarded for having
     * the wrong id — so `lastX` stopped updating, the drag measured as zero
     * travel, and the pane snapped back as though nothing had happened.
     *
     * That is not an exotic case. Dragging RIGHTWARD across a phone held in the
     * right hand puts the base of the thumb and the other fingers over the far
     * edge on the way, which is exactly why swiping BACK failed while swiping
     * forward was fine. Reported on device 2026-08-13.
     */
    if (gesture.current) return;
    if (startsInHorizontalScroller(e.target, wrapRef.current)) return;
    gesture.current = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      locked: false,
      dead: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g || g.dead || g.id !== e.pointerId) return;
    g.lastX = e.clientX;
    const moveX = e.clientX - g.startX;
    const moveY = e.clientY - g.startY;

    if (!g.locked) {
      // Give the vertical axis the benefit of the doubt: a drag has to be
      // clearly sideways (and past a real threshold) before the pager claims
      // it, so a slightly-diagonal flick down the page still scrolls.
      if (Math.abs(moveY) > 10 && Math.abs(moveY) >= Math.abs(moveX)) {
        g.dead = true;
        return;
      }
      if (Math.abs(moveX) < 14 || Math.abs(moveX) < Math.abs(moveY) * 1.5) return;
      g.locked = true;
      setMounted((prev) => {
        const next = new Set(prev);
        if (index > 0) next.add(index - 1);
        if (index < panes.length - 1) next.add(index + 1);
        return next;
      });
    }

    // Rubber-band past the first and last pane so the ends feel like ends.
    const atEdge = (moveX > 0 && index === 0) || (moveX < 0 && index === panes.length - 1);
    setDx(atEdge ? moveX * 0.3 : moveX);
  };

  /**
   * Settle wherever the drag actually got to.
   *
   * THE DISTANCE COMES FROM THE LAST MOVE, NOT FROM THE ENDING EVENT. This also
   * runs on `pointercancel` — which is the normal way a touch swipe ends here,
   * because the pager never preventDefaults, so the browser is free to take the
   * gesture back — and a pointercancel carries no meaningful position: engines
   * report the last point, the origin, or 0/0. With `e.clientX` reading 0, the
   * travelled distance came out as `-startX`: hugely negative, past the
   * threshold, for EVERY gesture. So every swipe turned the page to the RIGHT
   * whichever way you dragged, and the last pane simply never moved because
   * right was clamped away. Reported on device 2026-08-12.
   *
   * ONE GESTURE ENDS ONCE. Both `pointerup` and `pointercancel` arrive here, and
   * on touch BOTH can fire for the same drag — the browser reclaims the gesture
   * mid-swipe (cancel), then delivers the release (up). The second call used to
   * fall into the reset below and set `dx` back to 0 while the settle animation
   * from the first was still running, so the track slid to the next pane, slid
   * all the way back, and then jumped to that pane anyway when the transition
   * ended: two slide animations for one swipe. Reported on device 2026-08-12.
   * With no live gesture there is nothing to settle, so the second call returns
   * without touching anything.
   */
  const endGesture = (e: React.PointerEvent) => {
    const g = gesture.current;
    // Already settled by an earlier end event for this same drag.
    if (!g) return;
    // A stray finger lifting must not settle the drag the other one is still
    // making — the same asymmetry as the note in onPointerDown.
    if (e.pointerId !== g.id) return;
    gesture.current = null;
    if (g.dead || !g.locked) {
      if (dx !== 0) setDx(0);
      return;
    }
    const travelled = g.lastX - g.startX;
    const threshold = Math.min(72, Math.max(40, width() * 0.2));
    if (travelled <= -threshold) goTo(index + 1);
    else if (travelled >= threshold) goTo(index - 1);
    else goTo(index);
  };

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{
        position: "relative",
        // clip, NOT hidden. See the block comment: `hidden` would make this a
        // scroll container and break sticky filters inside a pane.
        overflowX: "clip",
        overflowY: "visible",
        // Keep the horizontal drag ours: see the block comment at the top.
        touchAction: "pan-y",
        // Reach the bottom of the screen so a swipe in the empty space under a
        // short pane still lands. See the note on `floorPx`; the vh value is
        // only the pre-measurement fallback.
        minHeight: floorPx != null ? `${floorPx}px` : "60vh",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endGesture}
      onPointerCancel={endGesture}
      data-testid="trade-pager"
    >
      <div
        style={{
          position: "relative",
          transform: `translate3d(${dx}px, 0, 0)`,
          transition: animating ? "transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)" : "none",
          willChange: dx !== 0 ? "transform" : undefined,
        }}
        onTransitionEnd={onTransitionEnd}
      >
        {panes.map((p, i) => {
          if (!mounted.has(i)) return null;
          const active = i === index;
          return (
            <div
              key={p.key}
              id={`trade-pane-${p.key}`}
              role="tabpanel"
              aria-labelledby={active ? labelledBy : undefined}
              aria-hidden={!active}
              data-testid={`trade-pane-${p.key}`}
              style={
                active
                  ? { position: "relative" }
                  : {
                      // Off-stage panes must not contribute height — that is
                      // what keeps a short pane from inheriting a long one's
                      // dead space.
                      position: "absolute",
                      top: 0,
                      left: `${(i - index) * 100}%`,
                      width: "100%",
                      pointerEvents: "none",
                    }
              }
            >
              {p.node}
            </div>
          );
        })}
      </div>
    </div>
  );
}
